'use client';

import { useEffect, useRef } from 'react';
import ReportModalAspectsSection from '@/components/products-report-modal/ReportModalAspectsSection';
import ReportModalSidebar from '@/components/products-report-modal/ReportModalSidebar';
import ReportModalTopAndTreemapSection from '@/components/products-report-modal/ReportModalTopAndTreemapSection';
import { type ProductsReportModalControllerBindings } from '@/components/products-report-modal/use-products-report-modal-controller';
import { QuotaActionButton } from '@/components/billing/QuotaTopUpButton';
import { UIPriority } from '@/enums/ui-priorities';
import Button from '@/ui/Button';
import Modal from '@/ui/Modal';
import { surfaceLayers } from '@/ui/overlay-layers';

const formatSelectedReviewsCountLabel = (count: number): string => {
    const mod10 = count % 10;
    const mod100 = count % 100;

    if (mod10 === 1 && mod100 !== 11) {
        return 'отзыв';
    }

    if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) {
        return 'отзыва';
    }

    return 'отзывов';
};

type ProductsReportModalViewProps = ProductsReportModalControllerBindings & {
    isShown: boolean;
    setIsShown: (value: boolean) => void;
    isAiAnalysisLoading: boolean;
    isAiAnalysisDisabled: boolean;
    aiAnalysisDisabledReason: string | null;
    isAiAnalysisQuotaExhausted: boolean;
    onRunAiAnalysis: () => void;
    onOpenProducts: () => void;
    onOpenBrands: () => void;
};

export const ProductsReportModalView = ({
    isShown,
    setIsShown,
    isAiAnalysisLoading,
    isAiAnalysisDisabled,
    aiAnalysisDisabledReason,
    isAiAnalysisQuotaExhausted,
    onRunAiAnalysis,
    onOpenProducts,
    onOpenBrands,
    companyName,
    hasSelectedReviewFilter,
    selectedReviewFilterCount,
    clearSelectedReviewFilter,
    staleDaysCount,
    freshRollupDaysCount,
    dirtyRollupDaysCount,
    generatedFromScratchDaysCount,
    summaryProcessedDays,
    summaryTotalDays,
    periodFrom,
    periodTo,
    setPeriodFrom,
    setPeriodTo,
    platformOptions,
    selectedPlatformValues,
    setSelectedPlatformValues,
    productGroupOptions,
    selectedProductGroupValues,
    setSelectedProductGroupValues,
    brandGroupOptions,
    selectedBrandGroupValues,
    setSelectedBrandGroupValues,
    canApplyMaxPeriod,
    applyMaxPeriod,
    totalReviews,
    totalAnswers,
    answersShare,
    averageResponseTimeMinutes,
    answeredReviewsCount,
    companyAverageRating,
    ratingDistributionChartData,
    averageRatingChartData,
    responseTimeChartData,
    channelsCount,
    productsCount,
    brandsCount,
    topChannels,
    topProducts,
    topBrands,
    treemapLayout,
    aspectRows,
    aspectRadarPoints,
    positiveHighlights,
    positiveHighlightsHiddenCount,
    negativeHighlights,
    negativeHighlightsHiddenCount,
    neutralHighlights,
    neutralHighlightsHiddenCount,
    isSummaryLoaded,
    isExportingPdf,
    exportPdf,
    closeModal,
}: ProductsReportModalViewProps) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const processingDetails = isSummaryLoaded
        ? `Обработка данных: акутальные — ${freshRollupDaysCount} дн., устаревшие — ${dirtyRollupDaysCount} дн., с нуля — ${generatedFromScratchDaysCount} дн.`
        : null;
    const redrawStatus = !isSummaryLoaded ? (
        <span
            role="status"
            aria-live="polite"
            className="inline-flex w-fit items-center gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-medium text-sky-700"
        >
            <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-sky-500" aria-hidden="true" />
            {summaryTotalDays > 0
                ? `Перерисовываем отчет... ${summaryProcessedDays}/${summaryTotalDays} дн.`
                : 'Перерисовываем отчет...'}
        </span>
    ) : null;

    useEffect(() => {
        if (!isShown) {
            return;
        }

        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer) {
            return;
        }

        if (typeof scrollContainer.scrollTo === 'function') {
            scrollContainer.scrollTo({ top: 0, left: 0 });
            return;
        }

        scrollContainer.scrollTop = 0;
    }, [isShown]);

    return (
        <Modal
            isShown={isShown}
            setIsShown={setIsShown}
            title="Отчет AIBACK"
            titlePrefix={redrawStatus}
            closable={true}
            className="h-[min(94vh,980px)] w-[min(1380px,98vw)]"
        >
            <div ref={scrollContainerRef} className="h-full w-full overflow-auto bg-slate-50 p-3 md:p-4">
                <div
                    data-testid="report-modal-actions"
                    className="sticky left-0 -mx-3 -mt-5 mb-4 border-b border-slate-200 bg-slate-50/95 px-3 pt-5 pb-3 backdrop-blur md:-mx-4 md:-mt-6 md:px-4 md:pt-6"
                    style={{ zIndex: surfaceLayers.surfaceStickyRaised }}
                >
                    <div className="mx-auto mb-3 w-full max-w-[1320px]">
                        {hasSelectedReviewFilter ? (
                            <p
                                data-testid="report-modal-selection-filter-summary"
                                className="text-sm font-medium text-slate-600"
                            >
                                <span className="text-slate-900">Отчет AIBACK</span>
                                <span>
                                    {`, выбрано: ${selectedReviewFilterCount} ${formatSelectedReviewsCountLabel(
                                        selectedReviewFilterCount,
                                    )} (`}
                                </span>
                                <button
                                    type="button"
                                    className="cursor-pointer text-sky-700 underline underline-offset-2 hover:text-sky-800"
                                    onClick={clearSelectedReviewFilter}
                                >
                                    сбросить фильтр
                                </button>
                                <span>)</span>
                            </p>
                        ) : null}
                        {processingDetails ? (
                            <p data-testid="report-modal-processing-details" className="mt-1 text-xs text-slate-500">
                                {processingDetails}
                            </p>
                        ) : null}
                        {staleDaysCount > 0 ? (
                            <p
                                data-testid="report-modal-stale-days-warning"
                                className="mt-1 text-sm font-medium text-amber-700"
                            >
                                {`Информация по ${staleDaysCount} дням, возможно, устаревшая.`}
                            </p>
                        ) : null}
                    </div>
                    <div className="mx-auto grid w-full max-w-[1320px] grid-cols-1 gap-2 xl:grid-cols-12">
                        <QuotaActionButton
                            reason={aiAnalysisDisabledReason}
                            isQuotaExhausted={isAiAnalysisDisabled && isAiAnalysisQuotaExhausted}
                            tooltipClassName="w-full xl:col-span-4"
                            className="w-full"
                            priorityType={UIPriority.PRIMARY}
                            onClick={onRunAiAnalysis}
                            type="button"
                            disabled={isAiAnalysisDisabled}
                        >
                            {isAiAnalysisLoading ? 'Анализ...' : 'ИИ-анализ'}
                        </QuotaActionButton>
                        <Button
                            className="w-full xl:col-span-4"
                            priorityType={UIPriority.PRIMARY}
                            onClick={() => {
                                void exportPdf();
                            }}
                            type="button"
                            disabled={isExportingPdf || !isSummaryLoaded}
                        >
                            {isExportingPdf ? 'Подготовка...' : 'Export PDF'}
                        </Button>
                        <Button
                            className="w-full xl:col-span-4"
                            priorityType={UIPriority.SECONDARY}
                            onClick={closeModal}
                            type="button"
                        >
                            Закрыть
                        </Button>
                    </div>
                </div>

                <div className="mx-auto flex w-full max-w-[1320px] flex-col gap-4">
                    <section data-testid="report-modal-page-1">
                        <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-12">
                            <ReportModalSidebar
                                companyName={companyName}
                                periodFrom={periodFrom}
                                periodTo={periodTo}
                                setPeriodFrom={setPeriodFrom}
                                setPeriodTo={setPeriodTo}
                                platformOptions={platformOptions}
                                selectedPlatformValues={selectedPlatformValues}
                                setSelectedPlatformValues={setSelectedPlatformValues}
                                productGroupOptions={productGroupOptions}
                                selectedProductGroupValues={selectedProductGroupValues}
                                setSelectedProductGroupValues={setSelectedProductGroupValues}
                                brandGroupOptions={brandGroupOptions}
                                selectedBrandGroupValues={selectedBrandGroupValues}
                                setSelectedBrandGroupValues={setSelectedBrandGroupValues}
                                canApplyMaxPeriod={canApplyMaxPeriod}
                                applyMaxPeriod={applyMaxPeriod}
                            />
                            <ReportModalTopAndTreemapSection
                                totalReviews={totalReviews}
                                totalAnswers={totalAnswers}
                                answersShare={answersShare}
                                companyAverageRating={companyAverageRating}
                                averageResponseTimeMinutes={averageResponseTimeMinutes}
                                answeredReviewsCount={answeredReviewsCount}
                                ratingDistributionChartData={ratingDistributionChartData}
                                averageRatingChartData={averageRatingChartData}
                                responseTimeChartData={responseTimeChartData}
                                channelsCount={channelsCount}
                                productsCount={productsCount}
                                brandsCount={brandsCount}
                                topChannels={topChannels}
                                topProducts={topProducts}
                                topBrands={topBrands}
                                onOpenProducts={onOpenProducts}
                                onOpenBrands={onOpenBrands}
                                onCloseModal={closeModal}
                                treemapLayout={treemapLayout}
                            />
                        </div>
                    </section>

                    <ReportModalAspectsSection
                        aspectRows={aspectRows}
                        aspectRadarPoints={aspectRadarPoints}
                        positiveHighlights={positiveHighlights}
                        positiveHighlightsHiddenCount={positiveHighlightsHiddenCount}
                        negativeHighlights={negativeHighlights}
                        negativeHighlightsHiddenCount={negativeHighlightsHiddenCount}
                        neutralHighlights={neutralHighlights}
                        neutralHighlightsHiddenCount={neutralHighlightsHiddenCount}
                    />
                </div>
            </div>
        </Modal>
    );
};
